import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import { getCategoryAndProductId } from 'analytics/snowplow/helpers/itemCategorisation'
import { getAirportsSearch } from 'api/flightsAirportSearch'
import { last } from 'lib/array/arrayUtils'
import type { MappedPlace } from 'lib/search/mappingUtils'
import { mapCityLevelPlace } from 'lib/search/mappingUtils'
import { getPlaceByLatLong } from 'api/search'
import type { SelfDescribingJson } from '@snowplow/browser-tracker'
import { createFlightItemContext } from 'analytics/snowplow/createFlightItemContext'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { FlightsFareTypes } from 'constants/flight'
import sanitizeItemContextMetadata from './sanitizeItemContextMetadata'
import { getAdultPrice, getFlightsTotal, isLuxPlusPriceDisplayed } from 'lib/flights/flightUtils'
import { floatify } from 'lib/maths/mathUtils'

async function getFlightCartItems(state: App.State, item: App.Checkout.FlightItem, isMultiCart?: boolean) {
  const searchFares = Object.values(state.flights.searchV2Flights)?.find(flights => flights?.searchId === item.searchId)?.fares
  if (!searchFares) return []

  const journeys = item.flights.map((flight, index) => searchFares[index]?.find(fare => fare.id === flight.journeyId))

  const existingJourneys = journeys.filter(journey => journey?.id)
  if (existingJourneys.length !== item.flights.length || journeys.length === 0) return []

  const firstJourney = journeys[0]?.flightGroup
  const firstJourneyFirstFlight = firstJourney?.flights[0]
  const firstJourneyLastFlight = last(firstJourney?.flights || [])
  const lastJourney = last(journeys)?.flightGroup
  const lastJourneyLastFlight = last(lastJourney?.flights || [])

  if (!firstJourneyFirstFlight || !lastJourneyLastFlight || !firstJourneyLastFlight) return []

  const price = floatify(getFlightsTotal(item))
  const { categoryId, productId } = getCategoryAndProductId(item.itemType)

  const isReturnOrOneWay = [FlightsFareTypes.RETURN, FlightsFareTypes.ONE_WAY].includes(item.fareType as FlightsFareTypes)

  const checkoutFirstLeg = item.flights[0]
  const checkoutLastLeg = last(item.flights)
  const travelStart = checkoutFirstLeg?.departingDate ?? firstJourneyFirstFlight.departingDate
  const travelEnd = getCheckoutLegTravelEnd(checkoutLastLeg) ?? lastJourneyLastFlight.arrivalDate

  return [comluxgroup.createItem_1_1_2({
    offerId: 'flight',
    categoryId,
    productId,
    duration: getFlightDurationInDays(travelStart, travelEnd),
    travelStart,
    travelEnd,
    numberOfAdults: item.occupants.adults,
    numberOfChildren: item.occupants.children,
    numberOfInfants: item.occupants.infants,
    childrenAges: item.occupants.childrenAge,
    currency: state.geo.currentCurrency,
    destinationCountry: isReturnOrOneWay ? firstJourneyLastFlight.arrivalCountry : lastJourneyLastFlight.arrivalCountry,
    originCountry: firstJourneyFirstFlight.departureCountry,
    originCity: firstJourneyFirstFlight.departureCity,
    destinationCity: isReturnOrOneWay ? firstJourneyLastFlight.arrivalCity : lastJourneyLastFlight.arrivalCity,
    typeId: item.fareType.toLowerCase(),
    itemId: item.itemId,
    price,
    metadata: sanitizeItemContextMetadata({
      isMultiCart,
      originAirportCode: checkoutFirstLeg?.departingAirportCode ?? firstJourneyFirstFlight.departureAirport,
      arrivalAirportCode: (isReturnOrOneWay
        ? checkoutFirstLeg?.arrivalAirportCode
        : checkoutLastLeg?.arrivalAirportCode) ??
        (isReturnOrOneWay ? firstJourneyFirstFlight.arrivalAirport : lastJourneyLastFlight.arrivalAirport),
    }),
  })]
}

function getCheckoutLegTravelEnd(flight?: App.Checkout.FlightItemFlight) {
  if (!flight) return undefined

  if (flight.arrivalDateTime) {
    return moment(flight.arrivalDateTime).format(ISO_DATE_FORMAT)
  }

  return flight.departingDate || undefined
}

function getFlightDurationInDays(departureDate?: string, arrivalDate?: string) {
  // default to 1 for one way
  const calculatedDuration = departureDate && arrivalDate ? moment(arrivalDate).diff(departureDate, 'days') : 1
  return calculatedDuration && calculatedDuration > 0 ? calculatedDuration : 1
}

async function getCityByAirportName(departingAirportName: string, arrivalAirportName: string) {
  const [departingAirportData, arrivalAirportData] = await Promise.all([getAirportsSearch(departingAirportName), getAirportsSearch(arrivalAirportName)])

  const departingLongitude = departingAirportData[0]?.lg
  const departingLatitude = departingAirportData[0]?.lt
  const arrivalLongitude = arrivalAirportData[0]?.lg
  const arrivalLatitude = arrivalAirportData[0]?.lt

  const getCitiesPromises: Array<Promise<MappedPlace | undefined>> = []

  getCitiesPromises.push(
    departingLongitude && departingLatitude
      ? getPlaceByLatLong(departingLatitude, departingLongitude).then(mapCityLevelPlace)
      : Promise.resolve(undefined),
  )

  getCitiesPromises.push(
    arrivalLongitude && arrivalLatitude
      ? getPlaceByLatLong(arrivalLatitude, arrivalLongitude).then(mapCityLevelPlace)
      : Promise.resolve(undefined),
  )

  const places = await Promise.all(getCitiesPromises)

  return {
    originCity: places[0]?.city,
    destinationCity: places[1]?.city,
  }
}

interface JourneyItemContextOverrides {
  originCity?: string
  destinationCity?: string
  /**
   * End of the trip the price covers, and what shape of trip it is. Both are needed under
   * `round_trip`, where the amount is a whole-trip total carried on the departing bound: the
   * journey's own arrival date and `fareType` describe the outbound leg alone, so trip-length and
   * one-way-vs-return cuts would silently mis-bucket the row.
   */
  travelEnd?: string
  fareType?: string
  memberPricePerAdult?: number
  nonMemberPricePerAdult?: number
  priceBasis?: 'round_trip' | 'per_bound'
  memberFareCount?: number
  selection?: 'carrier_deepest' | 'cheapest_member' | 'cheapest_public'
}

/**
 * Synchronous journey → flight item context mapping. Split out so the FLY-5724 impression can emit
 * several per search without paying `journeyToSnowplowItemContext`'s geocoding round-trips —
 * origin and destination already arrive on `search_context` there.
 */
export function journeyToFlightItemContext(
  journey: App.JourneyV2,
  currency: string,
  occupancy: App.Occupants,
  overrides: JourneyItemContextOverrides = {},
): SelfDescribingJson {
  const firstFlight = journey.flightGroup.flights[0]
  const lastFlight = last(journey.flightGroup.flights)
  // Dates must describe the same trip the price does — see `travelEnd` above.
  const arrivalDate = overrides.travelEnd ?? lastFlight.arrivalDate

  return createFlightItemContext({
    offerId: 'flight',
    originCity: overrides.originCity,
    destinationCity: overrides.destinationCity,
    airline: journey.carrier,
    arrivalAirportCode: lastFlight.arrivalAirport,
    departureDate: firstFlight.departingDate,
    duration: getFlightDurationInDays(firstFlight.departingDate, arrivalDate),
    fareType: overrides.fareType ?? journey.fareType.toLowerCase(),
    numberOfAdults: occupancy.adults,
    numberOfChildren: occupancy.children,
    numberOfInfants: occupancy.infants,
    childrenAges: occupancy.childrenAge,
    originAirportCode: firstFlight.departureAirport,
    pricePerAdult: journey.price.adult.totalFare!,
    arrivalDate,
    destinationCountry: lastFlight.arrivalCountry,
    originCountry: firstFlight.departureCountry,
    currency,
    memberPricePerAdult: overrides.memberPricePerAdult,
    nonMemberPricePerAdult: overrides.nonMemberPricePerAdult,
    priceBasis: overrides.priceBasis,
    memberFareCount: overrides.memberFareCount,
    selection: overrides.selection,
  })
}

export async function journeyToSnowplowItemContext(journey: App.JourneyV2, currency: string, occupancy: App.Occupants): Promise<SelfDescribingJson> {
  const firstFlight = journey.flightGroup.flights[0]
  const lastFlight = last(journey.flightGroup.flights)

  let cities: {
    originCity: string | undefined
    destinationCity: string | undefined
  } = { originCity: undefined, destinationCity: undefined }

  if (firstFlight?.departureAirportName && lastFlight?.arrivalAirportName) {
    try {
      cities = await getCityByAirportName(firstFlight?.departureAirportName, lastFlight?.arrivalAirportName)
    } catch { }
  }

  // On a LuxPlus+ fare `journey.price` *is* the member price, so `pricePerAdult` alone has silently
  // meant "member price sometimes". Carrying both makes it interpretable; `pricePerAdult` is
  // unchanged so existing dashboards keep working. Per-bound: no round-trip pairing to reason about
  // on a select event.
  const isMemberFare = isLuxPlusPriceDisplayed(journey)

  return journeyToFlightItemContext(journey, currency, occupancy, {
    originCity: cities?.originCity,
    destinationCity: cities?.destinationCity,
    memberPricePerAdult: isMemberFare ? getAdultPrice(journey.price, false) : undefined,
    nonMemberPricePerAdult: isMemberFare ? getAdultPrice(journey.regularPrice, false) : getAdultPrice(journey.price, false),
    priceBasis: 'per_bound',
  })
}

export default getFlightCartItems
